//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { SingleSelectControl, OptionType } from "@arangodb/ui";
import { Flex, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useMemo } from "react";
import { Props as ReactSelectProps } from "react-select";
import { NewDeploymentFormValues } from "./newDeploymentForm.types";
import { useFetchArangoPlatformBundles } from "./useFetchArangoPlatformBundles";
import { useReinitializeArangoPlatformBundle } from "./useReinitializeArangoPlatformBundle";
import { sortArangoPlatformBundlesByPriorityDesc } from "./sortArangoPlatformBundlesByPriority";

export const ArangoPlatformBundleSelector = ({ isDisabled }: { isDisabled?: boolean }) => {
  useReinitializeArangoPlatformBundle();
  const { values } = useFormikContext<NewDeploymentFormValues>();
  const regionId = values.regionId || "";
  const { data: bundles, isFetching } = useFetchArangoPlatformBundles({ regionId });

  const sortedBundles = useMemo(() => sortArangoPlatformBundlesByPriorityDesc(bundles?.items), [bundles?.items]);

  const bundleOptions = useMemo(() => {
    return sortedBundles.map((bundle) => ({
      label: bundle.name || bundle.id || "",
      value: bundle.id || "",
    })) as ReactSelectProps<OptionType, false>["options"];
  }, [sortedBundles]);

  const tooltip = useMemo(() => {
    if (!regionId) {
      return <Text>Choose a region first; available flavours depend on the region.</Text>;
    }
    return (
      <Flex direction="column" gap="2">
        <Text>Flavours extend the deployment with optional components. The dropdown lists them by priority (highest first).</Text>
        {isFetching ? (
          <Text>Loading flavours…</Text>
        ) : sortedBundles.length > 0 ? (
          sortedBundles.map((bundle) => {
            const title = bundle.name || bundle.id || "";
            return (
              <Text key={bundle.id || title}>
                {bundle.description ? (
                  <>
                    <Text as="span" fontWeight="semibold">
                      {title}:{" "}
                    </Text>
                    {bundle.description}
                  </>
                ) : (
                  <Text as="span" fontWeight="semibold">
                    {title}
                  </Text>
                )}
              </Text>
            );
          })
        ) : (
          <Text>No flavours are available for this region.</Text>
        )}
      </Flex>
    );
  }, [regionId, isFetching, sortedBundles]);

  return (
    <SingleSelectControl
      label="Flavour"
      name="arangodbPlatformBundle"
      isDisabled={isDisabled}
      selectProps={{
        options: bundleOptions,
        isLoading: isFetching,
        isDisabled: isFetching || isDisabled || !regionId,
      }}
      tooltip={tooltip}
    />
  );
};
